// Standup Projects tab — priority order, top to bottom:
//   1. ReleaseMasterCard (current version / master release),
//   2. focus project cards (ring + expand → ActionRegisterTable),
//   3. the shared <ProjectsTable> (star a row to escalate into focus).
import { useCallback, useEffect, useMemo, useState } from "react";
import { Box } from "@mui/material";
import { useSnackbar } from "notistack";
import {
  getAaronProjects, setProjectFocus, saveProject, getProject, getPlanNudges,
  type BrainProject,
} from "../../lib/brain-api";
import { useNavigate } from "react-router-dom";
import { Icon, Spinner, Button, Modal, TextField, SelectField, EmptyState } from "../../design/ui";
import {
  ProjectsTable,
  ReleaseMasterCard,
  FocusProjectCard,
  allProjectsForGlobalTab,
  focusProjectsForGlobalTab,
  type ActionRegisterPerson,
} from "../../mui";
import { brainTokens } from "../../design/brainTheme";

const c = brainTokens.color;

interface Person { userId: string; displayName: string }

export function ProjectsTab({ people, date, canEdit }: { people: Person[]; date: string; canEdit: boolean }) {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState<BrainProject[]>([]);
  const [metaById, setMetaById] = useState<Record<string, { owner?: string; fixVersion?: string }>>({});
  const [nudges, setNudges] = useState<Record<string, { openProposals: number; awaitingMySignoff: number; oldestOpenDays: number | null }>>({});
  const [newOpen, setNewOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [nName, setNName] = useState("");
  const [nKind, setNKind] = useState<"internal" | "customer">("internal");
  const [nCustomer, setNCustomer] = useState("");
  const [nEpic, setNEpic] = useState("");

  const peopleForRegister: ActionRegisterPerson[] = people;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const all = (await getAaronProjects({})).filter((p) => p.status !== "archived");
      setProjects(all);
      const focusIds = all.filter((p) => p.isFocus).map((p) => p.projectId).filter(Boolean);
      if (focusIds.length) {
        try { setNudges(await getPlanNudges(focusIds)); } catch { setNudges({}); }
        // Owner + optional fixVersion live on the registry Project.
        const entries = await Promise.all(
          focusIds.map(async (id) => {
            try {
              const p = await getProject(id);
              return [id, { owner: p.owner?.trim() || undefined, fixVersion: p.fixVersion?.trim() || undefined }] as const;
            } catch {
              return [id, {}] as const;
            }
          }),
        );
        setMetaById(Object.fromEntries(entries));
      } else {
        setNudges({});
        setMetaById({});
      }
    }
    catch (e) { enqueueSnackbar(e instanceof Error ? e.message : "Failed to load projects.", { variant: "error" }); }
    finally { setLoading(false); }
  }, [enqueueSnackbar]);
  useEffect(() => { void load(); }, [load]);

  // Support is banned from the COMPANY view (D1-c §3.1). The handoff §5.2 puts a
  // Support card here; Isaac overrode that — the Tickets tab owns the desk in
  // full, and Support belongs on a person's tab, only when they have tickets.
  // `hiddenFocus` is surfaced out loud rather than leaving a starred project
  // mysteriously missing.
  const { shown: focus, hidden: hiddenFocus } = useMemo(
    () => focusProjectsForGlobalTab(projects),
    [projects],
  );
  const tableProjects = useMemo(() => allProjectsForGlobalTab(projects), [projects]);
  const openProject = useCallback((projectId: string) => navigate(`/projects/${encodeURIComponent(projectId)}`), [navigate]);

  const createProject = useCallback(async () => {
    const name = nName.trim();
    if (!name) return;
    const projectId = name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
    const epic = nEpic.trim();
    setBusy(true);
    try {
      await saveProject({
        projectId, name, status: "planning",
        kind: nKind,
        customer: nKind === "customer" ? (nCustomer.trim() || undefined) : "aeronet",
        ...(epic ? { links: { jira_epic: epic, repos: [] } } : {}),
      });
      await setProjectFocus(projectId, true);
      enqueueSnackbar("Project created.", { variant: "success" });
      setNewOpen(false); setNName(""); setNCustomer(""); setNEpic(""); setNKind("internal");
      openProject(projectId);
    } catch (e) { enqueueSnackbar(e instanceof Error ? e.message : "Create failed.", { variant: "error" }); }
    finally { setBusy(false); }
  }, [nName, nKind, nCustomer, nEpic, enqueueSnackbar, openProject]);

  const toggleFocus = useCallback(async (projectId: string, next: boolean) => {
    setProjects((prev) => prev.map((p) => (p.projectId === projectId ? { ...p, isFocus: next } : p)));
    try {
      await setProjectFocus(projectId, next);
      if (next) { void load(); return; }
      enqueueSnackbar("Removed from focus", {
        action: (id) => (
          <button onClick={() => { closeSnackbar(id); void toggleFocus(projectId, true); }}
            style={{ background: "none", border: "none", color: "inherit", fontWeight: 700, fontSize: 13, cursor: "pointer", textDecoration: "underline" }}>Undo</button>
        ),
      });
    } catch (e) {
      setProjects((prev) => prev.map((p) => (p.projectId === projectId ? { ...p, isFocus: !next } : p)));
      enqueueSnackbar(e instanceof Error ? e.message : "Failed to change focus.", { variant: "error" });
    }
  }, [enqueueSnackbar, closeSnackbar, load]);

  return (
    <div>
      <ReleaseMasterCard canEdit={canEdit} />

      <Box sx={{ display: "flex", alignItems: "center", gap: 1.75, my: 2, flexWrap: "wrap" }}>
        <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, fontSize: 12, color: c.muted }}>
          <Icon name="folders" size={15} /> {focus.length} focus project{focus.length === 1 ? "" : "s"}
        </Box>
        {hiddenFocus.length > 0 && (
          <Box
            component="span"
            title="Support is a service desk shared by everyone. The Tickets tab owns it in full, and it appears on a person's tab only when they actually have tickets."
            sx={{ fontSize: 12, color: c.muted2, fontFamily: brainTokens.font.mono }}
          >
            support desk shown on the Tickets tab
          </Box>
        )}
        <Box sx={{ flex: 1 }} />
        {canEdit && <Button variant="primary" icon="plus" onClick={() => setNewOpen(true)}>New project</Button>}
      </Box>

      {loading ? (
        <Spinner label="Loading projects…" />
      ) : focus.length === 0 ? (
        <EmptyState icon="folder-star" title="No focus projects yet">
          {canEdit ? "Star a project in the table below to escalate it into focus." : "No focus projects have been selected yet."}
        </EmptyState>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mb: 1 }}>
          {focus.map((p) => (
            <Box key={p.projectId}>
              {(nudges[p.projectId]?.openProposals ?? 0) > 0 && (
                <Box
                  component="a"
                  href={`/projects/${encodeURIComponent(p.projectId)}?tab=plan`}
                  sx={{
                    display: "block",
                    mb: 0.75,
                    fontSize: 12.5,
                    color: c.tealInk,
                    background: c.tealTint,
                    borderRadius: `${brainTokens.radius.chip}px`,
                    px: 1,
                    py: 0.75,
                    textDecoration: "none",
                  }}
                >
                  {nudges[p.projectId]!.openProposals} suggested update
                  {nudges[p.projectId]!.openProposals === 1 ? "" : "s"} awaiting sign-off · Review
                </Box>
              )}
              <FocusProjectCard
                project={p}
                owner={metaById[p.projectId]?.owner ?? p.owner}
                fixVersion={metaById[p.projectId]?.fixVersion}
                nzDate={date}
                people={peopleForRegister}
                canEdit={canEdit}
              />
            </Box>
          ))}
        </Box>
      )}

      <Box sx={{ mt: 2.75 }}>
        <ProjectsTable
          projects={tableProjects}
          loading={loading}
          hideFocused
          canFocus={canEdit}
          onToggleFocus={(id, next) => void toggleFocus(id, next)}
          title="All projects"
        />
      </Box>

      <Modal
        title="New project"
        open={newOpen}
        onClose={() => setNewOpen(false)}
        actions={<>
          <Button onClick={() => setNewOpen(false)}>Cancel</Button>
          <Button variant="primary" disabled={busy || !nName.trim()} onClick={() => void createProject()}>Create</Button>
        </>}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <TextField label="Name" value={nName} placeholder="Inwards-goods rewrite" autoFocus
            onChange={(e) => setNName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") void createProject(); }} />
          <SelectField label="Kind" value={nKind} onChange={(e) => setNKind(e.target.value as "internal" | "customer")}>
            <option value="internal">Internal</option>
            <option value="customer">Customer</option>
          </SelectField>
          {nKind === "customer" && (
            <TextField label="Customer" value={nCustomer} placeholder="Customer name or id"
              onChange={(e) => setNCustomer(e.target.value)} />
          )}
          <TextField label="Jira epic" value={nEpic} placeholder="DO-2166"
            onChange={(e) => setNEpic(e.target.value)}
            hint="Epic key that tracks this project (optional)." />
        </div>
      </Modal>
    </div>
  );
}
